import React, { useState } from 'react';
import {
  SalvBanner,
  SalvBannerType,
  SalvButton,
  SalvFieldHeader,
  SalvModal,
  SalvTextField,
  DatePicker,
} from '@salvapp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Dayjs } from 'dayjs';
import { showFeedback } from '../../feedback/feedbackReducer';
import { parseErrorMessage } from '../../common/errors/helpers';
import { useRerunPeriodicScenarioForPeriod } from './scenarioRerunActions';

interface Props {
  show: boolean;
  onClose: () => void;
}

interface FormValues {
  scenarioId: string;
  from: Dayjs | null;
  to: Dayjs | null;
}

const validationSchema = Yup.object().shape({
  scenarioId: Yup.number().typeError('Must be a number').required('Scenario ID is required'),
  from: Yup.mixed<Dayjs>().required('From is required'),
  to: Yup.mixed<Dayjs>().required('To is required'),
});

const RerunPeriodicScenarioForPeriodModal = ({ show, onClose }: Props) => {
  const dispatch = useDispatch();
  const mutation = useRerunPeriodicScenarioForPeriod();
  const [error, setError] = useState<string | null>(null);

  const initialValues: FormValues = { scenarioId: '', from: null, to: null };

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    setError(null);
    try {
      await mutation.mutateAsync({
        scenarioId: Number(values.scenarioId),
        from: values.from!.toDate(),
        to: values.to!.toDate(),
      });
      dispatch(showFeedback({ message: 'Periodic scenario rerun for period triggered successfully', success: true }));
      onClose();
      resetForm();
    } catch (e) {
      setError(await parseErrorMessage(e));
    }
  };

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} validateOnMount onSubmit={handleSubmit}>
      {({ values, isValid, isSubmitting, setFieldValue, resetForm }) => (
        <SalvModal
          show={show}
          onHide={() => {
            resetForm();
            setError(null);
            onClose();
          }}
        >
          <Form>
            <SalvModal.Header>Rerun Periodic Scenario for a Time Period</SalvModal.Header>
            <SalvModal.Body>
              {error && <SalvBanner message={error} type={SalvBannerType.DANGER} clearable className='m-b-16' />}
              <SalvTextField
                label='Scenario ID'
                value={values.scenarioId}
                onChange={(value: string) => setFieldValue('scenarioId', value)}
                className='m-b-16'
              />
              <SalvFieldHeader label='From' className='m-b-4' />
              <DatePicker
                value={values.from}
                onChange={(date: Dayjs | null) => setFieldValue('from', date)}
                showTime
                className='m-b-16'
              />
              <SalvFieldHeader label='To' className='m-b-4' />
              <DatePicker value={values.to} onChange={(date: Dayjs | null) => setFieldValue('to', date)} showTime />
            </SalvModal.Body>
            <SalvModal.Footer>
              <SalvButton
                variant='muted'
                onClick={() => {
                  resetForm();
                  setError(null);
                  onClose();
                }}
                label='Cancel'
              />
              <SalvButton label='Rerun' type='submit' disabled={!isValid || isSubmitting || mutation.isPending} />
            </SalvModal.Footer>
          </Form>
        </SalvModal>
      )}
    </Formik>
  );
};

export default RerunPeriodicScenarioForPeriodModal;
